import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import { MfaFlow } from "@/components/MfaFlow";

const BG = "https://customer-assets-wrfwihn1.emergentagent.net/job_tenant-reimburse/artifacts/4ubcwpq8_image-1%20%283%29.jpeg";

export default function Login() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfa(data);
      } else {
        await checkAuth();
        toast.success(`Welcome back, ${data.name}`);
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onMfaSuccess = async (data) => {
    await checkAuth();
    toast.success(`Welcome back, ${data.name}`);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/30 via-transparent to-white/20" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Claim Q" className="h-9 w-9 object-contain" />
            <span className="font-heading font-semibold text-xl tracking-tight text-foreground">Claim Q</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          {mfa ? (
            <MfaFlow mfaData={mfa} onSuccess={onMfaSuccess} onCancel={() => { setMfa(null); setPassword(""); }} />
          ) : (
          <>
          <div className="mb-6">
            <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="login-title">Sign in</h1>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" placeholder="you@company.com" className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" placeholder="••••••••" className="rounded-sm" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-sm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
            </Button>
          </form>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
